import { Link } from 'react-router-dom';
import {
  STAGES, STAGE_LABEL, STAGE_ICON, SUB_LABEL, FLAG_LABEL, railState, timeline,
  type Journey, type Stage, type TripFilters,
} from '../../lib/tripPipeline';

const fmtDay = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');

// ── Pipeline strip: six stages, click one to filter ─────────────────────────

export function PipelineStrip({ counts, active, onPick }: {
  counts: Record<Stage, number>;
  active: Stage | '';
  onPick: (s: Stage | '') => void;
}) {
  const max = Math.max(1, ...STAGES.map((s) => counts[s]));
  return (
    <div className="mb-4 overflow-x-auto">
      <div className="flex min-w-[720px] items-stretch gap-1.5">
        {STAGES.map((s, i) => {
          const on = active === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => onPick(on ? '' : s)}
              aria-pressed={on}
              className={`relative flex-1 rounded-xl border px-3 py-3 text-left transition-colors ${on ? 'border-violet bg-violet/10' : 'border-app-border bg-app-card hover:bg-app-bg'}`}
            >
              <div className="flex items-center gap-1.5 text-[11px] text-app-muted">
                <i className={`ti ${STAGE_ICON[s]} text-[14px]`} />
                <span className="truncate">{STAGE_LABEL[s]}</span>
              </div>
              <div className="mt-1 text-2xl font-semibold text-app-fg">{counts[s]}</div>
              <div className="mt-2 h-1 rounded-full bg-app-border">
                <div className="h-1 rounded-full bg-violet" style={{ width: `${(counts[s] / max) * 100}%` }} />
              </div>
              {i < STAGES.length - 1 && (
                <i className="ti ti-chevron-right absolute -right-2.5 top-1/2 z-10 -translate-y-1/2 text-[14px] text-app-muted" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Journey rail: six dots, current one coloured by health ──────────────────

export function JourneyRail({ j }: { j: Journey }) {
  const off = j.stage === 'exited' || j.stage === 'abandoned';
  const attention = j.flags.length > 0;
  return (
    <div className="flex items-center" aria-label={`${STAGE_LABEL[j.stage]}`}>
      {STAGES.map((s, i) => {
        const st = railState(j, i);
        const tip = `${STAGE_LABEL[s]}${st === 'done' ? ' ✓' : ''}`;
        const dot =
          off ? 'bg-app-border'
          : st === 'done' ? 'bg-violet'
          : st === 'current' ? `bg-app-card ring-2 ${attention ? 'ring-gold' : 'ring-violet'}`
          : 'bg-app-border';
        return (
          <div key={s} className="flex items-center">
            <span title={tip} className={`block h-3 w-3 rounded-full ${dot}`} />
            {i < STAGES.length - 1 && (
              <span className={`block h-0.5 w-5 ${!off && st === 'done' ? 'bg-violet' : 'bg-app-border'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'gold' | 'crimson' | 'muted' | 'ok' }) {
  const cls = {
    gold: 'bg-[rgba(255,153,59,.12)] text-gold border-[rgba(255,153,59,.24)]',
    crimson: 'bg-crimson/10 text-crimson border-crimson/25',
    muted: 'bg-app-bg text-app-muted border-app-border',
    ok: 'bg-success/10 text-success border-success/25',
  }[tone];
  return <span className={`inline-flex items-center rounded-pill border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${cls}`}>{children}</span>;
}

export function JourneyBadges({ j }: { j: Journey }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {j.payment_status === 'paid' && <Badge tone="ok">Paid</Badge>}
      {j.payment_status === 'failed' && <Badge tone="crimson">Payment failed</Badge>}
      {j.payout_status === 'released' || j.payout_status === 'paid' ? <Badge tone="ok">Payout released</Badge> : null}
      {j.payment_status === 'paid' && j.payout_status === 'held' && <Badge tone="muted">Payout held</Badge>}
      {j.flags.map((f) => (
        <Badge key={f} tone={f === 'payment_failed' || f === 'refund_problem' || f === 'reported_problem' ? 'crimson' : 'gold'}>
          {FLAG_LABEL[f] ?? f}
        </Badge>
      ))}
    </div>
  );
}

// ── Flow list ───────────────────────────────────────────────────────────────

export function FlowList({ rows, onOpen }: { rows: Journey[]; onOpen: (j: Journey) => void }) {
  if (rows.length === 0) {
    return <div className="rounded-xl border border-app-border bg-app-card px-4 py-8 text-center text-xs text-app-muted">No journeys match these filters.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-app-border bg-app-card shadow-sm">
      <ul className="min-w-[760px] divide-y divide-app-border">
        {rows.map((j) => (
          <li key={j.journey_id}>
            <button type="button" onClick={() => onOpen(j)} className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-app-bg/50">
              <div className="w-56 min-w-0">
                <div className="truncate text-xs font-medium text-app-fg">{j.trip_name || (j.kind === 'lead' ? 'Chat with Alma' : 'Untitled trip')}</div>
                <div className="truncate text-[11px] text-app-muted">
                  {j.traveler_name ?? 'Traveler'}{j.host_name ? ` → ${j.host_name}` : ''}{j.city ? ` · ${j.city}` : ''}
                </div>
              </div>
              <JourneyRail j={j} />
              <div className="w-40 text-[11px]">
                <div className="text-app-fg">{SUB_LABEL[j.sub_state] ?? j.sub_state}</div>
                <div className="text-app-muted">{j.entered_approx ? '≈ ' : ''}{j.days_in_stage}d in {STAGE_LABEL[j.stage].toLowerCase()}</div>
              </div>
              <div className="flex-1"><JourneyBadges j={j} /></div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Detail side panel ───────────────────────────────────────────────────────

export function JourneyPanel({ j, onClose }: { j: Journey; onClose: () => void }) {
  const events = timeline(j);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside className="h-full w-full max-w-md overflow-y-auto border-l border-app-border bg-app-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-app-fg">{j.trip_name || (j.kind === 'lead' ? 'Chat with Alma' : 'Untitled trip')}</h2>
            <p className="text-xs text-app-muted">
              {j.traveler_name ?? 'Traveler'}{j.host_name ? ` → ${j.host_name}` : ''}{j.city ? ` · ${j.city}` : ''}
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-app-muted hover:bg-black/5">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="mt-4"><JourneyRail j={j} /></div>
        <p className="mt-2 text-xs text-app-fg">
          {STAGE_LABEL[j.stage]} · {SUB_LABEL[j.sub_state] ?? j.sub_state} · {j.entered_approx ? '≈ ' : ''}{j.days_in_stage}d
        </p>
        <div className="mt-2"><JourneyBadges j={j} /></div>
        <h3 className="mt-6 mb-2 text-xs font-semibold uppercase tracking-wide text-app-muted">History</h3>
        {events.length === 0 ? (
          <p className="text-xs text-app-muted">Nothing recorded yet.</p>
        ) : (
          <ol className="border-l border-app-border pl-4">
            {events.map((e) => (
              <li key={e.key} className="relative mb-3 text-xs">
                <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-violet" />
                <div className="text-app-fg">{e.label}</div>
                <div className="text-[11px] text-app-muted">{fmtDay(e.at)}</div>
              </li>
            ))}
          </ol>
        )}
        {j.match_id && (
          <Link to={`/admin/trips/${j.match_id}`} className="mt-4 inline-flex items-center gap-1 text-xs text-violet hover:underline">
            <i className="ti ti-eye" /> View as host (read-only)
          </Link>
        )}
      </aside>
    </div>
  );
}

// ── Shared filter bar ───────────────────────────────────────────────────────

const selectCls = 'rounded-lg border border-app-border bg-app-card px-2.5 py-1.5 text-xs text-app-fg';

export function TripFilterBar({ f, set, hosts, cities, total, onExport }: {
  f: TripFilters;
  set: (patch: Partial<TripFilters>) => void;
  hosts: { id: string; name: string }[];
  cities: string[];
  total: number;
  onExport: () => void;
}) {
  const check = (label: string, key: 'attention' | 'showAbandoned' | 'showExited' | 'showArchived') => (
    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-app-fg">
      <input type="checkbox" checked={f[key]} onChange={(e) => set({ [key]: e.target.checked })} className="h-3.5 w-3.5 accent-violet" />
      {label}
    </label>
  );
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <input
        type="search" placeholder="Search trip, traveler, host…" value={f.q}
        onChange={(e) => set({ q: e.target.value })} className={`${selectCls} w-56`}
      />
      <select aria-label="Host" value={f.host} onChange={(e) => set({ host: e.target.value })} className={selectCls}>
        <option value="">All hosts</option>
        {hosts.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select>
      <select aria-label="City" value={f.city} onChange={(e) => set({ city: e.target.value })} className={selectCls}>
        <option value="">All cities</option>
        {cities.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select aria-label="Payment" value={f.payment} onChange={(e) => set({ payment: e.target.value })} className={selectCls}>
        <option value="">Any payment</option>
        <option value="none">No payment</option>
        <option value="pending">Pending</option>
        <option value="paid">Paid</option>
        <option value="failed">Failed</option>
        <option value="refunded">Refunded</option>
      </select>
      <select aria-label="Handbook" value={f.handbook} onChange={(e) => set({ handbook: e.target.value })} className={selectCls}>
        <option value="">Any handbook</option>
        <option value="none">None</option>
        <option value="draft">Draft</option>
        <option value="submitted">Sent for approval</option>
        <option value="approved">Approved</option>
      </select>
      <select aria-label="Survey" value={f.survey} onChange={(e) => set({ survey: e.target.value })} className={selectCls}>
        <option value="">Any survey</option>
        <option value="none">Not sent</option>
        <option value="sent">Sent</option>
        <option value="answered">Answered</option>
      </select>
      <select aria-label="Date field" value={f.dateField} onChange={(e) => set({ dateField: e.target.value as TripFilters['dateField'] })} className={selectCls}>
        <option value="created">Created</option>
        <option value="start">Trip start</option>
      </select>
      <input type="date" aria-label="From" value={f.from} onChange={(e) => set({ from: e.target.value })} className={selectCls} />
      <input type="date" aria-label="To" value={f.to} onChange={(e) => set({ to: e.target.value })} className={selectCls} />
      <select aria-label="Sort" value={f.sort} onChange={(e) => set({ sort: e.target.value as TripFilters['sort'] })} className={selectCls}>
        <option value="stuck">Longest in stage</option>
        <option value="soonest">Soonest start</option>
        <option value="newest">Newest</option>
      </select>
      <div className="flex flex-wrap items-center gap-3">
        {check('Needs attention', 'attention')}
        {check('Abandoned chats', 'showAbandoned')}
        {check('Exited', 'showExited')}
        {check('Archived', 'showArchived')}
      </div>
      <span className="ml-auto text-xs text-app-muted">{total} journey{total === 1 ? '' : 's'}</span>
      <button type="button" onClick={onExport} className="inline-flex items-center gap-1 rounded-lg border border-app-border px-2.5 py-1.5 text-xs text-app-fg hover:bg-app-bg">
        <i className="ti ti-download text-[13px]" /> CSV
      </button>
    </div>
  );
}
